import { createContext, useContext } from "react";
import { StyleProp, TextStyle, ViewStyle } from "react-native";
import { Color } from "react-native-svg";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

export type tPdTextStyle = TextStyle & { color?: Color };

export type tPdButtonColorScheme = "green" | "purple" | "pink" | "red" | "gray" | "white";

export type tPdButtonSize = "lg" | "md" | "sm";

export type tPdButtonVariant = "solid" | "ghost" | "outline";

interface iButtonContext {
  colorScheme: tPdButtonColorScheme;
  size: tPdButtonSize;
  variant: tPdButtonVariant;
  pressed: boolean;
  hovered?: boolean;
  focused?: boolean;
}

export const ButtonContext = createContext<Partial<iButtonContext>>({});

const getColorFromScheme = (colorScheme?: tPdButtonColorScheme, reverse?: boolean) => {
  if (colorScheme === "purple") {
    return reverse ? kColors.white : kColors.brandPurple;
  } else if (colorScheme === "pink") {
    return reverse ? kColors.brandGreen : kColors.brandPink2;
  } else if (colorScheme === "red") {
    return reverse ? kColors.white : kColors.secondaryRed;
  } else if (colorScheme === "gray") {
    return reverse ? kColors.brandGreen : kColors.lightGray;
  } else if (colorScheme === "white") {
    return reverse ? kColors.brandGreen : kColors.white;
  }
  return reverse ? kColors.white : kColors.brandGreen;
};

export const useButtonStyles = (isDisabled?: boolean): ViewStyle => {
  const { colorScheme, size, variant, hovered, pressed } = useContext(ButtonContext);
  const color = getColorFromScheme(colorScheme, false);

  const styles: StyleProp<ViewStyle> = {
    height: 28,
    backgroundColor: color,
    borderColor: color,
    alignItems: "center",
    borderWidth: 1,
    borderRadius: kStyleConst.kSpacing,
    flexDirection: "row",
    justifyContent: "center",
    paddingHorizontal: kStyleConst.kSpacing * 2,
  };

  if (size === "lg") {
    styles.height = 38;
    styles.paddingHorizontal = kStyleConst.kSpacing * 4;
    styles.borderRadius = kStyleConst.kSpacing * 1.3;
  }

  if (size === "sm") {
    styles.height = 22;
    styles.paddingHorizontal = kStyleConst.kSpacing * 1.3;
  }

  if (variant === "outline") {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = color;
  }

  if (variant === "ghost") {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = kColors.transparent;
  }

  if (hovered) {
    styles.opacity = 0.8;
  }

  if (isDisabled || pressed) {
    styles.opacity = 0.6;
  }

  return styles;
};

export const useIconButtonStyles = (isDisabled?: boolean): ViewStyle => {
  const { colorScheme, size, variant, hovered, pressed } = useContext(ButtonContext);
  const color = getColorFromScheme(colorScheme, false);

  const styles: StyleProp<ViewStyle> = {
    height: 40,
    width: 40,
    backgroundColor: color,
    borderColor: color,
  };

  if (size === "sm") {
    styles.height = 32;
    styles.width = 32;
  }

  if (size === "lg") {
    styles.height = 56;
    styles.width = 56;
  }

  if (variant === "outline" || hovered) {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = color;
  }

  if (variant === "ghost") {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = kColors.transparent;
  }

  if (isDisabled || pressed) {
    styles.opacity = 0.6;
  }

  return styles;
};

export const useButtonLabelStyles = (): tPdTextStyle => {
  const { colorScheme, size, variant } = useContext(ButtonContext);

  const styles: StyleProp<tPdTextStyle> = {
    color: getColorFromScheme(colorScheme, variant === "solid"),
    fontSize: 13,
    fontFamily: "Poppins",
    fontWeight: "300",
    letterSpacing: 1.2,
    textAlign: "center",
  };

  if (size === "sm") {
    styles.fontSize = 12;
  }

  if (size === "lg") {
    styles.fontSize = 14;
  }

  return styles;
};
